import { useEffect, useState } from 'react';
import { Trophy, Plus, Play, Check, X, Clock, Flame, Share2, Trash2, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePlayer } from '@/contexts/PlayerContext';
import ShareModal from '@/components/ShareModal';

export default function Challenges() {
    const { user } = useAuth();
    const { playTrack, setFullscreen } = usePlayer();
    const [data, setData] = useState({ featured: [], personal: [] });
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [detail, setDetail] = useState(null);
    const [share, setShare] = useState(null);

    const refresh = async () => {
        try {
            const { data } = await api.get('/challenges');
            setData(data);
        } finally { setLoading(false); }
    };
    useEffect(() => { refresh(); }, []);

    const openDetail = async (challengeId) => {
        const { data } = await api.get(`/challenges/${challengeId}`);
        setDetail(data);
    };

    const join = async (id) => {
        await api.post(`/challenges/${id}/join`);
        await refresh();
        if (detail?.challenge?.id === id) await openDetail(id);
    };
    const leave = async (id) => {
        if (!window.confirm('Leave this challenge? Your progress will be cleared.')) return;
        await api.delete(`/challenges/${id}/leave`);
        await refresh();
        if (detail?.challenge?.id === id) await openDetail(id);
    };
    const deletePersonal = async (id) => {
        if (!window.confirm('Delete this personal challenge?')) return;
        await api.delete(`/challenges/${id}`);
        setDetail(null);
        refresh();
    };

    const playMeditation = (m) => {
        playTrack({
            id: m.id, title: m.title, subtitle: m.instructor,
            image: m.cover_image, audio_url: m.audio_url,
            kind: 'meditation', duration_minutes: m.duration_minutes,
        });
        setFullscreen(true);
    };

    return (
        <div className="space-y-8" data-testid="challenges-page">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-forest-soft">Challenges</p>
                    <h1 className="font-serif text-4xl md:text-5xl text-forest mt-2">Small arcs of practice</h1>
                    <p className="text-forest-soft mt-2 max-w-xl">
                        A few days, a small goal. Just enough structure to hold the practice — never enough to shame you for missing a day.
                    </p>
                </div>
                <button onClick={() => setShowForm(true)} data-testid="challenge-new-button" className="qm-btn-primary inline-flex items-center gap-2 self-start">
                    <Plus size={16} /> Personal challenge
                </button>
            </header>

            {loading ? (
                <div className="qm-card p-12 grid place-items-center">
                    <Loader2 className="animate-spin text-forest" />
                </div>
            ) : (
                <>
                    <section className="space-y-4" data-testid="featured-section">
                        <h2 className="font-serif text-2xl text-forest">Featured</h2>
                        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {data.featured.map((c) => (
                                <ChallengeCard key={c.challenge.id} c={c} onOpen={() => openDetail(c.challenge.id)} />
                            ))}
                        </ul>
                    </section>

                    <section className="space-y-4" data-testid="personal-section">
                        <h2 className="font-serif text-2xl text-forest">Your personal challenges</h2>
                        {data.personal.length === 0 ? (
                            <div className="qm-card p-8 text-center text-forest-soft">
                                Nothing here yet — tap <span className="text-forest font-medium">Personal challenge</span> to set your own small goal.
                            </div>
                        ) : (
                            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                {data.personal.map((c) => (
                                    <ChallengeCard key={c.challenge.id} c={c} onOpen={() => openDetail(c.challenge.id)} />
                                ))}
                            </ul>
                        )}
                    </section>
                </>
            )}

            {showForm && (
                <CreatePersonalModal
                    onClose={() => setShowForm(false)}
                    onCreated={async () => { setShowForm(false); await refresh(); }}
                />
            )}

            {detail && (
                <ChallengeDetailModal
                    data={detail}
                    user={user}
                    onClose={() => setDetail(null)}
                    onJoin={() => join(detail.challenge.id)}
                    onLeave={() => leave(detail.challenge.id)}
                    onDelete={() => deletePersonal(detail.challenge.id)}
                    onPlay={playMeditation}
                    onShare={() => setShare({
                        card: 'challenge',
                        title: detail.challenge.title,
                        reward: detail.challenge.reward_title,
                        sessions: detail.challenge.goal_sessions,
                        days: detail.challenge.duration_days,
                        name: user?.name?.split(' ')[0],
                    })}
                />
            )}

            {share && <ShareModal data={share} onClose={() => setShare(null)} />}
        </div>
    );
}

function ChallengeCard({ c, onOpen }) {
    const { challenge, enrolled, participant_count, progress } = c;
    const cover = challenge.cover_image || 'https://images.unsplash.com/photo-1504198453319-5ce911bafcde?auto=format&fit=crop&w=800&q=80';
    return (
        <li className="qm-card overflow-hidden group hover:-translate-y-1 hover:shadow-lg transition-all" data-testid={`challenge-card-${challenge.id}`}>
            <button onClick={onOpen} className="w-full text-left">
                <div
                    className="aspect-[5/3] relative"
                    style={{
                        backgroundImage: `linear-gradient(180deg, transparent 40%, rgba(45,74,62,0.45)), url(${cover})`,
                        backgroundSize: 'cover', backgroundPosition: 'center',
                    }}
                >
                    <span className="absolute top-3 left-3 qm-pill bg-cream/90 text-forest text-xs border-transparent">
                        <Trophy size={12} className="inline -mt-0.5 mr-1" /> {challenge.reward_title}
                    </span>
                    {enrolled && (
                        <span className="absolute top-3 right-3 qm-pill bg-forest text-cream text-xs border-transparent">
                            {progress?.is_completed ? 'Completed' : `${progress?.percent ?? 0}%`}
                        </span>
                    )}
                </div>
                <div className="p-5">
                    <div className="flex items-center gap-2 text-xs text-forest-soft">
                        <span className="qm-pill bg-sage/30 border-transparent px-3 py-1">{challenge.category}</span>
                        <span className="inline-flex items-center gap-1"><Clock size={12} /> {challenge.duration_days}d</span>
                        <span className="inline-flex items-center gap-1"><Flame size={12} /> {challenge.goal_sessions} sessions</span>
                    </div>
                    <h3 className="font-serif text-xl text-forest mt-3">{challenge.title}</h3>
                    <p className="text-sm text-forest-soft mt-1 line-clamp-2">{challenge.description}</p>
                    {enrolled && progress && (
                        <div className="mt-3">
                            <div className="h-1.5 rounded-full bg-forest/10 overflow-hidden">
                                <div className="h-full bg-forest transition-[width] duration-500" style={{ width: `${progress.percent}%` }} />
                            </div>
                            <div className="text-xs text-forest-soft mt-1">{progress.sessions_completed} / {progress.goal_sessions} sessions · {progress.days_remaining}d left</div>
                        </div>
                    )}
                    {!enrolled && challenge.kind === 'featured' && (
                        <div className="text-xs text-forest-soft mt-3">{participant_count} already joined</div>
                    )}
                </div>
            </button>
        </li>
    );
}

function ChallengeDetailModal({ data, user, onClose, onJoin, onLeave, onDelete, onPlay, onShare }) {
    const { challenge, enrolled, participant_count, progress, suggested_meditations } = data;
    const cover = challenge.cover_image || 'https://images.unsplash.com/photo-1504198453319-5ce911bafcde?auto=format&fit=crop&w=1200&q=80';
    return (
        <div className="fixed inset-0 z-50 bg-forest/30 backdrop-blur-sm grid place-items-center p-3 animate-fade-in" role="dialog" data-testid="challenge-detail">
            <div className="w-full max-w-2xl qm-card overflow-hidden max-h-[90vh] overflow-y-auto qm-scroll">
                <div
                    className="relative aspect-[21/9]"
                    style={{
                        backgroundImage: `linear-gradient(180deg, rgba(45,74,62,0.12), rgba(45,74,62,0.55)), url(${cover})`,
                        backgroundSize: 'cover', backgroundPosition: 'center',
                    }}
                >
                    <button onClick={onClose} data-testid="detail-close" aria-label="Close" className="absolute top-3 right-3 rounded-full w-10 h-10 grid place-items-center bg-white/80 hover:bg-white text-forest">
                        <X size={18} />
                    </button>
                    <div className="absolute bottom-4 left-5 right-5 text-cream">
                        <div className="text-xs uppercase tracking-[0.25em] opacity-90 flex items-center gap-2">
                            <span>{challenge.category}</span>
                            <span>·</span>
                            <span>{challenge.difficulty}</span>
                        </div>
                        <h2 className="font-serif text-3xl md:text-4xl mt-1">{challenge.title}</h2>
                    </div>
                </div>
                <div className="p-6 md:p-8 space-y-5">
                    <p className="text-forest-soft">{challenge.description}</p>
                    <div className="grid grid-cols-3 gap-3">
                        <StatBox label="Days" value={challenge.duration_days} />
                        <StatBox label="Sessions" value={challenge.goal_sessions} />
                        <StatBox label="Reward" value={challenge.reward_title} small />
                    </div>

                    {enrolled && progress && (
                        <div className="qm-card p-5 bg-sage/20" data-testid="detail-progress">
                            <div className="flex items-center justify-between text-sm text-forest-soft">
                                <span>{progress.sessions_completed} / {progress.goal_sessions} sessions</span>
                                <span>{progress.days_remaining}d left</span>
                            </div>
                            <div className="h-2 rounded-full bg-forest/10 overflow-hidden mt-2">
                                <div className="h-full bg-forest transition-[width] duration-500" style={{ width: `${progress.percent}%` }} />
                            </div>
                            {progress.is_completed && (
                                <div className="mt-3 inline-flex items-center gap-2 bg-forest text-cream rounded-full px-4 py-2 text-sm animate-bloom">
                                    <Check size={16} /> Completed — beautifully done.
                                </div>
                            )}
                            {progress.is_expired && !progress.is_completed && (
                                <div className="mt-3 text-xs text-forest-soft italic">This arc has passed. You can start fresh any time.</div>
                            )}
                        </div>
                    )}

                    {suggested_meditations?.length > 0 && (
                        <div>
                            <h3 className="font-serif text-xl text-forest mb-3">Suggested sessions</h3>
                            <ul className="grid sm:grid-cols-2 gap-2" data-testid="detail-suggested">
                                {suggested_meditations.map((m, i) => (
                                    <li key={m.id} className="rounded-2xl border border-border p-3 flex items-center gap-3">
                                        <div
                                            className="w-12 h-12 rounded-xl bg-cover bg-center shrink-0"
                                            style={{ backgroundImage: `url(${m.cover_image})` }}
                                        />
                                        <div className="min-w-0 flex-1">
                                            <div className="text-xs text-forest-soft">Day {i + 1} · {m.duration_minutes}m</div>
                                            <div className="text-sm text-forest truncate">{m.title}</div>
                                        </div>
                                        <button onClick={() => onPlay(m)} aria-label={`Play ${m.title}`} data-testid={`detail-play-${m.id}`} className="rounded-full w-10 h-10 grid place-items-center bg-forest text-cream hover:scale-105 active:scale-95 transition">
                                            <Play size={16} className="ml-0.5" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="flex flex-wrap items-center gap-2 pt-2">
                        {!enrolled && (
                            <button onClick={onJoin} data-testid="detail-join" className="qm-btn-primary inline-flex items-center gap-2">
                                <Flame size={16} /> Join challenge
                            </button>
                        )}
                        {enrolled && (
                            <button onClick={onLeave} data-testid="detail-leave" className="qm-btn-ghost inline-flex items-center gap-2">
                                Leave
                            </button>
                        )}
                        {enrolled && progress?.is_completed && (
                            <button onClick={onShare} data-testid="detail-share" className="qm-btn-primary inline-flex items-center gap-2">
                                <Share2 size={16} /> Share the moment
                            </button>
                        )}
                        {challenge.kind === 'personal' && challenge.created_by === user?.id && (
                            <button onClick={onDelete} data-testid="detail-delete-personal" className="ml-auto qm-btn-ghost text-red-700 inline-flex items-center gap-1">
                                <Trash2 size={14} /> Delete
                            </button>
                        )}
                        {challenge.kind === 'featured' && (
                            <span className="ml-auto text-xs text-forest-soft">{participant_count} joined</span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function StatBox({ label, value, small = false }) {
    return (
        <div className="rounded-2xl border border-border p-3">
            <div className="text-xs uppercase tracking-wider text-forest-soft">{label}</div>
            <div className={`font-serif text-forest mt-1 ${small ? 'text-base' : 'text-2xl'}`}>{value}</div>
        </div>
    );
}

function CreatePersonalModal({ onClose, onCreated }) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [days, setDays] = useState(7);
    const [sessions, setSessions] = useState(7);
    const [reward, setReward] = useState('Done with grace');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true); setError('');
        try {
            await api.post('/challenges', {
                title: title.trim(),
                description: description.trim(),
                duration_days: Number(days),
                goal_sessions: Number(sessions),
                reward_title: reward.trim() || 'Done with grace',
            });
            onCreated();
        } catch (err) {
            setError(err?.response?.data?.detail || 'Could not create your challenge.');
        } finally { setSubmitting(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-forest/30 backdrop-blur-sm grid place-items-center p-3 animate-fade-in" role="dialog">
            <form onSubmit={submit} className="w-full max-w-lg qm-card p-6 md:p-8 space-y-4" data-testid="personal-challenge-form">
                <div className="flex items-center justify-between">
                    <h2 className="font-serif text-2xl text-forest">A small goal, your way</h2>
                    <button type="button" onClick={onClose} className="rounded-full w-9 h-9 grid place-items-center text-forest hover:bg-dusty/40" aria-label="Close">
                        <X size={16} />
                    </button>
                </div>
                <div>
                    <label className="text-sm text-forest-soft" htmlFor="pc-title">Title</label>
                    <input id="pc-title" required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Sundays in silence" className="qm-input mt-1" data-testid="pc-title-input" />
                </div>
                <div>
                    <label className="text-sm text-forest-soft" htmlFor="pc-desc">Why (optional)</label>
                    <input id="pc-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What will this give you?" className="qm-input mt-1" data-testid="pc-desc-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="text-sm text-forest-soft" htmlFor="pc-days">Over how many days?</label>
                        <input id="pc-days" type="number" min={3} max={90} value={days} onChange={(e) => setDays(e.target.value)} className="qm-input mt-1" data-testid="pc-days-input" />
                    </div>
                    <div>
                        <label className="text-sm text-forest-soft" htmlFor="pc-sessions">How many sessions?</label>
                        <input id="pc-sessions" type="number" min={1} max={200} value={sessions} onChange={(e) => setSessions(e.target.value)} className="qm-input mt-1" data-testid="pc-sessions-input" />
                    </div>
                </div>
                <div>
                    <label className="text-sm text-forest-soft" htmlFor="pc-reward">A name for the reward</label>
                    <input id="pc-reward" value={reward} onChange={(e) => setReward(e.target.value)} placeholder="Keeper of Calm" className="qm-input mt-1" data-testid="pc-reward-input" />
                </div>
                {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3">{error}</div>}
                <button type="submit" disabled={submitting} data-testid="pc-submit" className="qm-btn-primary w-full disabled:opacity-60">
                    {submitting ? 'Setting intention…' : 'Create challenge'}
                </button>
            </form>
        </div>
    );
}
